import { create } from "zustand";
import { persist } from "zustand/middleware";

export type NotificationSoundType = "newOrder" | "newRequest" | "newChat" | "kdsNewOrder";

interface AudioNotificationSettings {
  enabled: boolean;
  volume: number; // 0-1
  sounds: {
    newOrder: boolean;
    newRequest: boolean;
    newChat: boolean;
    kdsNewOrder: boolean;
  };
}

interface AudioNotificationStore {
  settings: AudioNotificationSettings;
  audioContext: AudioContext | null;
  setSettings: (
    updater:
      | Partial<AudioNotificationSettings>
      | ((prev: AudioNotificationSettings) => AudioNotificationSettings)
  ) => void;
  toggle: () => void;
  getOrCreateAudioContext: () => AudioContext | null;
}

const DEFAULT_SETTINGS: AudioNotificationSettings = {
  enabled: false,
  volume: 0.7,
  sounds: {
    newOrder: true,
    newRequest: true,
    newChat: true,
    kdsNewOrder: true,
  },
};

export const useAudioNotificationStore = create<AudioNotificationStore>()(
  persist(
    (set, get) => ({
      settings: DEFAULT_SETTINGS,
      audioContext: null,

      setSettings: (updater) =>
        set((state) => {
          if (typeof updater === "function") {
            return { settings: updater(state.settings) };
          }
          return { settings: { ...state.settings, ...updater } };
        }),

      toggle: () =>
        set((state) => ({
          settings: { ...state.settings, enabled: !state.settings.enabled },
        })),

      getOrCreateAudioContext: () => {
        const state = get();
        if (state.audioContext) {
          // Resume if suspended
          if (state.audioContext.state === "suspended") {
            state.audioContext.resume();
          }
          return state.audioContext;
        }

        try {
          const ctx = new AudioContext();
          set({ audioContext: ctx });
          return ctx;
        } catch {
          return null;
        }
      },
    }),
    {
      name: "guestivo_audio_settings",
      // Only persist settings, not the AudioContext
      partialize: (state) => ({ settings: state.settings }),
    }
  )
);
